/**
 * OBSERVABILITY-S1 — wrapper de init do Sentry no frontend.
 *
 * Por que dynamic import?
 *   Repo está em React 19; @sentry/react@8 peer-dep React >=16 (ok) mas
 *   o cache npm local tem permissão root → `npm install` falha. Vercel
 *   build em prod faz install limpo (sem o problema). Estratégia:
 *   - dev local sem a lib → noop silencioso
 *   - prod (Vercel) com a lib + REACT_APP_SENTRY_DSN → init real
 *
 * Princípios:
 *   - DSN ausente → no-op total
 *   - PII scrubbing OBRIGATÓRIO via beforeSend:
 *       - URL: strip query string (pode ter ?token=...)
 *       - Body de fetch: não capturado (default Sentry)
 *       - Replays desabilitados (privacidade + quota)
 *   - traces 0.10 (free tier compatible)
 *   - Falha graciosa: import error, init error → log + segue
 *
 * Exporta:
 *   initSentry()                — chama 1× no index.js (top-level await OK)
 *   SentryErrorBoundary(children) — wrapper pro <App />
 *     Quando lib ausente, retorna children direto (sem fallback UI custom)
 */

const _dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
const _env = process.env.REACT_APP_ENV || "production";
const _release = process.env.REACT_APP_GIT_SHA || "dev";

let _initialized = false;
let _sentryModule = null;

/**
 * Tenta carregar @sentry/react e inicializar. Idempotente.
 * Retorna Promise<boolean>. true = inicializado, false = no-op.
 */
export async function initSentry() {
  if (_initialized) return true;
  if (!_dsn) {
    if (typeof console !== "undefined") {
      console.info("[sentry] DSN ausente — error tracking desativado");
    }
    return false;
  }

  try {
    const Sentry = await import(/* webpackIgnore: true */ "@sentry/react").catch(() => null);
    if (!Sentry || !Sentry.init) {
      console.info("[sentry] @sentry/react não instalado — skip");
      return false;
    }
    _sentryModule = Sentry;

    Sentry.init({
      dsn: _dsn,
      environment: _env,
      release: _release,
      tracesSampleRate: 0.10,
      // Privacidade + quota free: replay completamente off.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // PII scrubbing.
      beforeSend(event /*, hint */) {
        try {
          // 1. URL: remove query string (pode conter ?token=, ?key=)
          if (event.request?.url && typeof event.request.url === "string") {
            event.request.url = event.request.url.split("?")[0];
          }
          // 2. Headers: remove Authorization/Cookie defensivo
          if (event.request?.headers && typeof event.request.headers === "object") {
            for (const k of Object.keys(event.request.headers)) {
              const lk = k.toLowerCase();
              if (lk === "authorization" || lk === "cookie" || lk === "x-api-key") {
                delete event.request.headers[k];
              }
            }
          }
          // 3. User PII: corta email/username (defesa em prof)
          if (event.user) {
            delete event.user.email;
            delete event.user.username;
            delete event.user.ip_address;
          }
          // 4. Breadcrumbs com URL — limpa query string também
          if (Array.isArray(event.breadcrumbs)) {
            for (const b of event.breadcrumbs) {
              if (b?.data?.url && typeof b.data.url === "string") {
                b.data.url = b.data.url.split("?")[0];
              }
            }
          }
        } catch {
          // Se scrub falhar, melhor descartar o event do que vazar PII.
          return null;
        }
        return event;
      },
    });
    _initialized = true;
    console.info(`[sentry] inicializado (env=${_env}, release=${_release})`);
    return true;
  } catch (e) {
    console.warn("[sentry] init falhou (non-fatal):", e?.message || e);
    return false;
  }
}

/**
 * Wrapper React pra Sentry.ErrorBoundary. Dynamic-aware:
 * Se Sentry não foi carregado, retorna `children` direto (sem boundary).
 *
 * Uso no index.js:
 *   import { SentryBoundary } from "./utils/sentryInit";
 *   root.render(<SentryBoundary><App /></SentryBoundary>);
 */
import React from "react";

export function SentryBoundary({ children }) {
  // Se módulo carregado, usa Sentry.ErrorBoundary; senão children direto.
  if (_sentryModule && _sentryModule.ErrorBoundary) {
    const EB = _sentryModule.ErrorBoundary;
    return (
      <EB
        fallback={({ resetError }) => (
          <div
            role="alert"
            style={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#0B0F1A",
              color: "#F1F5F9",
              padding: "2rem",
              fontFamily: "system-ui, sans-serif",
              textAlign: "center",
            }}
          >
            <div>
              <h1 style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>
                Algo deu errado
              </h1>
              <p style={{ color: "#94A3B8", marginBottom: "1.5rem" }}>
                Tentamos recuperar mas a página travou. Recarregue, por favor.
              </p>
              <button
                type="button"
                onClick={() => {
                  try { resetError?.(); } catch { /* */ }
                  window.location.reload();
                }}
                style={{
                  padding: "0.5rem 1.25rem",
                  borderRadius: "0.5rem",
                  background: "rgba(6,182,212,0.1)",
                  border: "1px solid rgba(6,182,212,0.3)",
                  color: "#06b6d4",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                }}
              >
                Recarregar
              </button>
            </div>
          </div>
        )}
      >
        {children}
      </EB>
    );
  }
  return children;
}

// Test-only helpers
export function _isInitialized() {
  return _initialized;
}
export function _resetForTests() {
  _initialized = false;
  _sentryModule = null;
}
